import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { InView } from 'react-intersection-observer';
import { withEndlessScroll } from '../../../HOC/endlessScroll';
import { setError } from '../../../../actions';
import { AgencyFilterResultsList } from '../AgencyFilterResultsList';
import { ItemCardPlaceholder } from '../../../Placeholders/ItemCardPlaceholder';
import { ItemCard } from '../../../ui/ItemCard';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { filteredAgenciesQuery } from '../../../../api/queries';
import { Category } from '../AgencyFilterResults/types';
import { FilteredAgenciesQuery } from '../../../../graphql/graphql';
import {
  buttonStyles,
  ResultBlockContainer,
  ResultBlockTitle,
  ShowAllAdsText,
  TitleContainer
} from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import { getCardsLimit } from '../getCardsLimit';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import { getFilterParamsToGetAgencyItems } from '../../../../utils/helpers/item';
import { SectionPlaceholder } from '../../../Placeholders/SectionPlaceholder';

const filterResultsBlockRef = React.createRef<HTMLDivElement>();
const EndlessSearchItems = withEndlessScroll(AgencyFilterResultsList);

interface AgencyFilterResultsBlockProps {
  title: string;
  category: Category;
  agencyId?: string;
  activeCategoryId: string;
  isLeased: boolean;
  limit: number;
  onClickItem: (id: string, isSetLeasedCategories?: boolean) => void;
  setActiveLeasedCategory?: React.Dispatch<React.SetStateAction<string[]>>;
}

export const AgencyFilterResultsBlock: React.FC<AgencyFilterResultsBlockProps> =
  memo(
    ({
      title,
      category,
      agencyId,
      activeCategoryId,
      isLeased,
      limit,
      onClickItem,
      setActiveLeasedCategory
    }) => {
      const [skipRequest, setSkipRequest] = useState(true);
      const { t } = useTranslation();
      const dispatch = useDispatch();
      const { isBookmarked } = useBookmarks();
      const { isSmall, isLarge, isSmallOrLargeDesktop } = useScreenSize();

      const cardsLimit = getCardsLimit(isLarge, isSmall);

      const variables = {
        filter: getFilterParamsToGetAgencyItems(
          isLeased,
          agencyId,
          category.id,
          activeCategoryId
        ),
        first: limit
      };

      const { loading, data, error, fetchMore } =
        useQuery<FilteredAgenciesQuery>(filteredAgenciesQuery, {
          variables,
          skip: skipRequest,
          ssr: false,
          fetchPolicy: 'network-only'
        });

      const result = data?.itemsConnection?.edges || [];
      const totalCount = data?.itemsConnection?.totalCount;
      const isShowButton = result.length === limit && (totalCount || 0) > limit;

      useEffect(() => {
        if (error) dispatch(setError(true));
      }, [error]);

      if (totalCount === 0) {
        return null;
      }

      if (loading) {
        return (
          <SectionPlaceholder noHeader>
            {Array.from({ length: 4 }, (_, index) => (
              <ItemCardPlaceholder key={index} isCardsLayout />
            ))}
          </SectionPlaceholder>
        );
      }

      const showAllResultHandler = () => {
        if (activeCategoryId === 'all') {
          onClickItem(category.id);
        } else {
          onClickItem(category.id, false);
          setActiveLeasedCategory?.([activeCategoryId]);
        }
      };

      const showAllAdsText = t('pages.agency.show_all_results_many', {
        count: totalCount
      });

      return (
        <InView
          as="div"
          onChange={inView => {
            if (inView) {
              setSkipRequest(false);
            }
          }}
        >
          <ResultBlockContainer data-cy="agency-filter__results-block">
            <TitleContainer>
              {!loading && title && (
                <ResultBlockTitle
                  data-cy="agency-filter__results-block__header"
                  tag={isSmallOrLargeDesktop ? 'h2' : 'h3'}
                >
                  {title}
                </ResultBlockTitle>
              )}

              {!isSmall && isShowButton && (
                <ShowAllAdsText
                  onClick={showAllResultHandler}
                  tag="body_1"
                  data-cy="show-all-results-button"
                >
                  {showAllAdsText}
                </ShowAllAdsText>
              )}
            </TitleContainer>

            <EndlessSearchItems
              items={result || []}
              loading={loading}
              ref={filterResultsBlockRef}
              isStopLoad={
                !data?.itemsConnection.pageInfo.hasNextPage ||
                limit === cardsLimit
              }
              onLoadMore={() => {
                const moreSearchItemsQueryVariables = {
                  filter: getFilterParamsToGetAgencyItems(
                    isLeased,
                    agencyId,
                    category.id,
                    activeCategoryId
                  ),
                  first: limit,
                  cursor: data?.itemsConnection.pageInfo.endCursor
                };

                fetchMore({
                  variables: moreSearchItemsQueryVariables
                });
              }}
            >
              {result.map(item => (
                <ItemCard
                  key={item?.node?.id}
                  {...item?.node}
                  inBookmarks={isBookmarked(item?.node?.id)}
                />
              ))}
            </EndlessSearchItems>
            {isShowButton && isSmall && (
              <Button
                cypress="show-all-results-button"
                text={showAllAdsText}
                onClick={showAllResultHandler}
                state={ButtonType.edit}
                styles={buttonStyles}
              />
            )}
          </ResultBlockContainer>
        </InView>
      );
    }
  );

AgencyFilterResultsBlock.displayName = `AgencyFilterResultsBlock`;
